import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import router, { menuRoutes, menuRouteEnum } from '@/router'
import { useNotify } from '@/composables/notify'
import { usePlanningStore } from '@/stores/planning.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePrintTemplates } from '@/composables/print-template'
import { warningCodeEnum } from '@/stores/portal.store'

// Key used for the "archive instead of delete" option in the project modal.
const ARCHIVE_KEY = 'archive'

const extractWarning = (error) => error?.response?.data?.details?.[0]?.errors?.[0] || {}

/**
 * Generic core for the multi-option delete flow. Given a store delete fn (and optional archive
 * fn), it exposes the dialog state, an `open(item, error)` to populate it from a backend 409
 * response, and a `select(key)` that re-issues the delete with the chosen `mode` (or archives).
 *
 * Shared by the grids, the mobile views (through their edit dialogs) and the edit dialogs so the
 * dispatch logic is not duplicated per call site.
 */
const createDeleteOptions = ({ onDelete, onArchive, buildDialog, onDeleted, archivedMessageKey = 'info.archivedSuccessfully' }) => {
  const i18n = getI18n()
  const { notifySuccess, notifyError } = useNotify()

  const deleteOptions = ref({
    show: false,
    title: '',
    message: '',
    caption: '',
    options: [],
    item: null,
  })

  const openDeleteOptions = (item, error) => {
    const warning = extractWarning(error)
    deleteOptions.value = { show: true, item, ...buildDialog(warning.params || [], item) }
  }

  const onDeleteOptionSelect = async(key) => {
    const item = deleteOptions.value.item
    if (!item) {
      return
    }
    try {
      if (key === ARCHIVE_KEY) {
        await onArchive?.(item.id)
        notifySuccess(i18n.t(archivedMessageKey))
      } else {
        await onDelete(item.id, { params: { mode: key }, skipErrorMessage: true })
        notifySuccess(i18n.t('info.deletedSuccessfully'))
      }
      await onDeleted?.(key, item)
    } catch {
      notifyError(i18n.t('error.deleteFailed'))
    }
  }

  const deleteAsync = async(item) => {
    try {
      await onDelete(item.id, { skipErrorMessage: true })
      notifySuccess(i18n.t('info.deletedSuccessfully'))
      await onDeleted?.(null, item)
    } catch (error) {
      if (error.response?.status === 409) {
        openDeleteOptions(item, error)
      } else {
        notifyError(i18n.t('error.deleteFailed'))
      }
    }
  }

  return { deleteOptions, openDeleteOptions, onDeleteOptionSelect, deleteAsync }
}

export const useProjectDeleteOptions = ({ onDeleted } = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const mode = planningStore.projectDeleteModeEnum

  return createDeleteOptions({
    onDelete: (id, config) => planningStore.deleteProjectAsync(id, config),
    onArchive: (id) => planningStore.archiveProjectAsync(id),
    onDeleted,
    buildDialog: (params) => {
      const state = params[0] || {}
      return {
        title: i18n.t('common.confirmDelete'),
        message: i18n.t('warning.projectDeleteOptions'),
        caption: `${i18n.t('warning.projectDeleteState', {
          allocations: state.allocations_count || 0,
          rows: state.quote_rows_count || 0,
        })} — ${i18n.t('warning.archiveKeepsAllocations')}`,
        options: [
          { key: mode.PROJECT_AND_QUOTE, label: i18n.t('common.deleteProjectAndQuote'), icon: 'mdi-delete-sweep', color: 'negative' },
          { key: mode.PROJECT_ONLY, label: i18n.t('common.deleteOnlyProject'), icon: 'mdi-delete-outline', color: 'negative' },
          { key: mode.ALLOCATIONS_ONLY, label: i18n.t('common.deleteOnlyAllocations'), icon: 'mdi-calendar-remove', color: 'warning' },
          { key: ARCHIVE_KEY, label: i18n.t('common.archive'), icon: 'mdi-archive', color: 'primary', standalone: true },
        ],
      }
    },
  })
}

export const useWarehouseDeleteOptions = ({ onDeleted } = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const accountingStore = useAccountingStore()
  const { notifyWarning } = useNotify()

  const notifyUsedInCompanyConfig = () => notifyWarning(i18n.t('warning.warehouseIsDefault'), {
    timeout: 10000,
    actions: [{
      label: i18n.t('common.openCompanySettings'),
      color: 'dark',
      handler: () => router.push({
        path: menuRoutes[menuRouteEnum.COMPANY],
        query: { section: accountingStore.companyDataSectionEnum.LOCATIONS },
      }),
    }],
  })

  const options = createDeleteOptions({
    onDelete: (id, config) => planningStore.deleteWarehouseAsync(id, config),
    onArchive: (id) => planningStore.zeroAndArchiveWarehouseAsync(id),
    onDeleted,
    buildDialog: (params) => ({
      title: i18n.t('common.warning'),
      message: i18n.t('warning.warehouseArchiveOptions'),
      caption: params.map(p => p.is_staff
        ? `${p.service_name} (${i18n.t('common.associated')})`
        : `${p.service_name}: ${p.quantity}`).join(', '),
      options: [
        { key: ARCHIVE_KEY, label: i18n.t('common.emptyLocationAndArchive'), icon: 'mdi-archive-arrow-down', color: 'warning', standalone: true },
      ],
    }),
  })

  const onWarehouseBlocked = (item, error) => {
    if (extractWarning(error).code === warningCodeEnum.WAREHOUSE_IS_DEFAULT) {
      notifyUsedInCompanyConfig()
      return
    }
    options.openDeleteOptions(item, error)
  }

  return { ...options, onWarehouseBlocked }
}

export const useQuoteDeleteOptions = ({ onDeleted } = {}) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const mode = accountingStore.quoteDeleteModeEnum

  return createDeleteOptions({
    onDelete: (id, config) => accountingStore.deleteBudgetDocumentAsync(id, config),
    onDeleted,
    buildDialog: () => ({
      title: i18n.t('common.confirmDelete'),
      message: i18n.t('warning.quoteDeleteOptions'),
      caption: null,
      options: [
        { key: mode.QUOTE_AND_PROJECTS, label: i18n.t('common.deleteQuoteAndProjects'), icon: 'mdi-delete-sweep', color: 'negative' },
        { key: mode.QUOTE_ONLY, label: i18n.t('common.deleteOnlyQuote'), icon: 'mdi-delete-outline', color: 'negative' },
      ],
    }),
  })
}

export const usePrintTemplateDeleteOptions = ({ onDeleted } = {}) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const { templateById, defaultIdFor } = usePrintTemplates()
  const mode = accountingStore.printTemplateDeleteModeEnum
  const { QUOTE, INVOICE } = accountingStore.printDocumentTypeEnum

  return createDeleteOptions({
    onDelete: (id, config) => accountingStore.deleteDocumentPrintTemplateAsync(id, config),
    onArchive: (id) => accountingStore.upsertDocumentPrintTemplateAsync({ id, is_available: false }),
    archivedMessageKey: 'info.updatedSuccessfully',
    onDeleted,
    buildDialog: (params) => {
      const usage = params[0] || {}
      const issuedInvoices = Number(usage.issued_invoice_count || 0)
      const message = issuedInvoices
        ? i18n.t('warning.printTemplateUsedByIssuedInvoices', { invoices: issuedInvoices })
        : i18n.t('warning.printTemplateDeleteOptions', {
          quotes: Number(usage.quote_count || 0),
          invoices: Number(usage.invoice_count || 0),
          quoteDefault: templateById(defaultIdFor(QUOTE))?.name,
          invoiceDefault: templateById(defaultIdFor(INVOICE))?.name,
        })
      return {
        title: i18n.t('common.confirmDelete'),
        message,
        caption: null,
        options: [
          ...(issuedInvoices ? [] : [{ key: mode.REASSIGN_TO_DEFAULTS, label: i18n.t('common.printTemplateMoveAndDelete'), icon: 'mdi-delete-outline', color: 'negative' }]),
          { key: ARCHIVE_KEY, label: i18n.t('common.printTemplateHideInstead'), icon: 'mdi-eye-off-outline', color: 'primary', standalone: true },
        ],
      }
    },
  })
}
